import supabase from '../supabaseClient';

// ============================================================================
// RIVALRY MANAGEMENT
// ============================================================================

/**
 * Create a new rivalry
 * @param {Object} rivalryData - Rivalry details
 * @returns {Object} { data, error }
 */
export async function createRivalry({
  name,
  description,
  maxParticipants = 10,
  rivalryType = 'standard',
  settings = {},
}) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  // Create the rivalry
  const { data: rivalry, error: rivalryError } = await supabase
    .from('rivalries')
    .insert({
      name,
      description,
      max_participants: maxParticipants,
      rivalry_type: rivalryType,
      created_by: user.id,
      settings,
      status: 'active',
    })
    .select()
    .single();

  if (rivalryError) {
    return { data: null, error: rivalryError };
  }

  // Automatically add creator as first participant
  const { error: participantError } = await supabase
    .from('rivalry_participants')
    .insert({
      rivalry_id: rivalry.id,
      user_id: user.id,
      is_active: true,
    });

  if (participantError) {
    console.error('Error adding creator as participant:', participantError);
  }

  return { data: rivalry, error: null };
}

/**
 * Get all rivalries for the current user
 * @returns {Object} { data, error }
 */
export async function getUserRivalries() {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('rivalries')
    .select(`
      *,
      rivalry_participants!inner(
        user_id,
        monthly_points,
        current_rank
      )
    `)
    .eq('rivalry_participants.user_id', user.id)
    .order('created_at', { ascending: false });

  return { data, error };
}

/**
 * Get rivalry details by ID
 * @param {string} rivalryId - Rivalry UUID
 * @returns {Object} { data, error }
 */
export async function getRivalryById(rivalryId) {
  const { data, error } = await supabase
    .from('rivalries')
    .select('*')
    .eq('id', rivalryId)
    .single();

  return { data, error };
}

/**
 * Update rivalry details
 * @param {string} rivalryId - Rivalry UUID
 * @param {Object} updates - Fields to update
 * @returns {Object} { data, error }
 */
export async function updateRivalry(rivalryId, updates) {
  const { data, error } = await supabase
    .from('rivalries')
    .update(updates)
    .eq('id', rivalryId)
    .select()
    .single();

  return { data, error };
}

// ============================================================================
// PARTICIPANTS
// ============================================================================

/**
 * Get all participants in a rivalry
 * @param {string} rivalryId - Rivalry UUID
 * @returns {Object} { data, error }
 */
export async function getRivalryParticipants(rivalryId) {
  const { data, error } = await supabase
    .from('rivalry_participants')
    .select(`
      *,
      fighter_profiles(
        fighter_name,
        tagline,
        profile_image_url,
        record_wins,
        record_losses
      )
    `)
    .eq('rivalry_id', rivalryId)
    .eq('is_active', true)
    .order('current_rank', { ascending: true });

  return { data, error };
}

/**
 * Join a rivalry
 * @param {string} rivalryId - Rivalry UUID
 * @param {string} nickname - Optional nickname
 * @returns {Object} { data, error }
 */
export async function joinRivalry(rivalryId, nickname = null) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('rivalry_participants')
    .insert({
      rivalry_id: rivalryId,
      user_id: user.id,
      nickname,
      is_active: true,
    })
    .select()
    .single();

  return { data, error };
}

/**
 * Leave a rivalry
 * @param {string} rivalryId - Rivalry UUID
 * @returns {Object} { data, error }
 */
export async function leaveRivalry(rivalryId) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('rivalry_participants')
    .delete()
    .eq('rivalry_id', rivalryId)
    .eq('user_id', user.id);

  return { data, error };
}

// ============================================================================
// LEADERBOARD
// ============================================================================

/**
 * Get rivalry leaderboard using the helper function
 * @param {string} rivalryId - Rivalry UUID
 * @returns {Object} { data, error }
 */
export async function getRivalryLeaderboard(rivalryId) {
  const { data, error } = await supabase
    .rpc('get_rivalry_leaderboard', { p_rivalry_id: rivalryId });

  return { data, error };
}

/**
 * Get user's current stats in a rivalry
 * @param {string} rivalryId - Rivalry UUID
 * @returns {Object} { data, error }
 */
export async function getUserRivalryStats(rivalryId) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('rivalry_participants')
    .select('*')
    .eq('rivalry_id', rivalryId)
    .eq('user_id', user.id)
    .single();

  return { data, error };
}

// ============================================================================
// INVITATIONS
// ============================================================================

/**
 * Create rivalry invitation
 * @param {string} rivalryId - Rivalry UUID
 * @param {string} email - Optional email to invite
 * @returns {Object} { data, error }
 */
export async function createInvitation(rivalryId, email = null) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  // Generate unique invitation token
  const invitationToken = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;

  const { data, error } = await supabase
    .from('rivalry_invitations')
    .insert({
      rivalry_id: rivalryId,
      invited_by: user.id,
      email,
      invitation_token: invitationToken,
      status: 'pending',
    })
    .select()
    .single();

  return { data, error };
}

/**
 * Get invitation by token
 * @param {string} token - Invitation token
 * @returns {Object} { data, error }
 */
export async function getInvitationByToken(token) {
  const { data, error } = await supabase
    .from('rivalry_invitations')
    .select(`
      *,
      rivalries(
        id,
        name,
        description,
        current_participants,
        max_participants
      )
    `)
    .eq('invitation_token', token)
    .eq('status', 'pending')
    .single();

  return { data, error };
}

/**
 * Accept rivalry invitation
 * @param {string} invitationId - Invitation UUID
 * @returns {Object} { data, error }
 */
export async function acceptInvitation(invitationId) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  // Get invitation details
  const { data: invitation, error: invError } = await supabase
    .from('rivalry_invitations')
    .select('*')
    .eq('id', invitationId)
    .single();

  if (invError || !invitation) {
    return { data: null, error: invError || new Error('Invitation not found') };
  }

  // Join the rivalry
  const { error: joinError } = await joinRivalry(invitation.rivalry_id);

  if (joinError) {
    return { data: null, error: joinError };
  }

  // Update invitation status
  const { data, error } = await supabase
    .from('rivalry_invitations')
    .update({ status: 'accepted', accepted_at: new Date().toISOString() })
    .eq('id', invitationId)
    .select()
    .single();

  return { data, error };
}

/**
 * Decline rivalry invitation
 * @param {string} invitationId - Invitation UUID
 * @returns {Object} { data, error }
 */
export async function declineInvitation(invitationId) {
  const { data, error } = await supabase
    .from('rivalry_invitations')
    .update({ status: 'declined' })
    .eq('id', invitationId)
    .select()
    .single();

  return { data, error };
}

// ============================================================================
// FIGHTER PROFILES
// ============================================================================

/**
 * Get fighter profile for current user
 * @returns {Object} { data, error }
 */
export async function getFighterProfile() {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('fighter_profiles')
    .select('*')
    .eq('user_id', user.id)
    .single();

  return { data, error };
}

/**
 * Get fighter profile by user ID
 * @param {string} userId - User UUID
 * @returns {Object} { data, error }
 */
export async function getFighterProfileByUserId(userId) {
  const { data, error } = await supabase
    .from('fighter_profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  return { data, error };
}

/**
 * Update fighter profile
 * @param {Object} updates - Fields to update
 * @returns {Object} { data, error }
 */
export async function updateFighterProfile(updates) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('fighter_profiles')
    .update(updates)
    .eq('user_id', user.id)
    .select()
    .single();

  return { data, error };
}

// ============================================================================
// MESSAGES
// ============================================================================

/**
 * Get messages for a rivalry
 * @param {string} rivalryId - Rivalry UUID
 * @param {number} limit - Number of messages to fetch
 * @returns {Object} { data, error }
 */
export async function getRivalryMessages(rivalryId, limit = 50) {
  const { data, error } = await supabase
    .from('rivalry_messages')
    .select(`
      *,
      fighter_profiles(
        fighter_name,
        profile_image_url
      )
    `)
    .eq('rivalry_id', rivalryId)
    .order('created_at', { ascending: false })
    .limit(limit);

  return { data: data?.reverse(), error };
}

/**
 * Send a message to a rivalry
 * @param {string} rivalryId - Rivalry UUID
 * @param {string} content - Message content
 * @returns {Object} { data, error }
 */
export async function sendMessage(rivalryId, content) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('rivalry_messages')
    .insert({
      rivalry_id: rivalryId,
      user_id: user.id,
      message_type: 'user',
      content,
    })
    .select()
    .single();

  return { data, error };
}

// ============================================================================
// WORKOUT POINTS
// ============================================================================

/**
 * Record workout points
 * @param {Object} workoutData - Workout details and points
 * @returns {Object} { data, error }
 */
export async function recordWorkoutPoints({
  rivalryId,
  workoutType,
  workoutName,
  durationMinutes,
  basePoints = 100,
  difficultyMultiplier = 1.0,
  performanceMultiplier = 1.0,
  streakBonus = 0,
}) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  // Calculate total points
  const totalPoints = Math.round(
    (basePoints * difficultyMultiplier * performanceMultiplier) + streakBonus
  );

  // Get active season
  const { data: season } = await supabase
    .from('seasons')
    .select('id')
    .eq('rivalry_id', rivalryId)
    .eq('status', 'active')
    .single();

  const { data, error } = await supabase
    .from('workout_points')
    .insert({
      rivalry_id: rivalryId,
      user_id: user.id,
      season_id: season?.id,
      workout_type: workoutType,
      workout_name: workoutName,
      duration_minutes: durationMinutes,
      base_points: basePoints,
      difficulty_multiplier: difficultyMultiplier,
      performance_multiplier: performanceMultiplier,
      streak_bonus: streakBonus,
      total_points: totalPoints,
      calculation_details: {
        base: basePoints,
        difficulty: difficultyMultiplier,
        performance: performanceMultiplier,
        streak: streakBonus,
        final: totalPoints,
      },
    })
    .select()
    .single();

  if (!error) {
    // Update participant stats
    await supabase.rpc('increment', {
      table_name: 'rivalry_participants',
      column_name: 'monthly_points',
      amount: totalPoints,
      row_id: user.id,
    });
  }

  return { data, error };
}

/**
 * Get workout history for a rivalry
 * @param {string} rivalryId - Rivalry UUID
 * @param {number} limit - Number of workouts to fetch
 * @returns {Object} { data, error }
 */
export async function getWorkoutHistory(rivalryId, limit = 20) {
  const { data, error } = await supabase
    .from('workout_points')
    .select(`
      *,
      fighter_profiles(
        fighter_name,
        profile_image_url
      )
    `)
    .eq('rivalry_id', rivalryId)
    .order('workout_date', { ascending: false })
    .limit(limit);

  return { data, error };
}

// ============================================================================
// SEASONS
// ============================================================================

/**
 * Get current active season for a rivalry
 * @param {string} rivalryId - Rivalry UUID
 * @returns {Object} { data, error }
 */
export async function getCurrentSeason(rivalryId) {
  const { data, error } = await supabase
    .from('seasons')
    .select('*')
    .eq('rivalry_id', rivalryId)
    .eq('status', 'active')
    .single();

  return { data, error };
}

/**
 * Get season history for a rivalry
 * @param {string} rivalryId - Rivalry UUID
 * @returns {Object} { data, error }
 */
export async function getSeasonHistory(rivalryId) {
  const { data, error } = await supabase
    .from('seasons')
    .select('*')
    .eq('rivalry_id', rivalryId)
    .order('season_number', { ascending: false });

  return { data, error };
}

/**
 * Get season results
 * @param {string} seasonId - Season UUID
 * @returns {Object} { data, error }
 */
export async function getSeasonResults(seasonId) {
  const { data, error } = await supabase
    .from('season_results')
    .select(`
      *,
      fighter_profiles(
        fighter_name,
        profile_image_url,
        record_wins,
        record_losses
      )
    `)
    .eq('season_id', seasonId)
    .order('final_rank', { ascending: true });

  return { data, error };
}

// ============================================================================
// BADGES & TITLES
// ============================================================================

/**
 * Get user's earned badges
 * @returns {Object} { data, error }
 */
export async function getUserBadges() {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('earned_badges')
    .select(`
      *,
      badge_definitions(*)
    `)
    .eq('user_id', user.id)
    .order('earned_at', { ascending: false });

  return { data, error };
}

/**
 * Get user's earned titles
 * @returns {Object} { data, error }
 */
export async function getUserTitles() {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('earned_titles')
    .select(`
      *,
      title_definitions(*)
    `)
    .eq('user_id', user.id)
    .order('earned_at', { ascending: false });

  return { data, error };
}

// ============================================================================
// CREDITS
// ============================================================================

/**
 * Get user's credit balance
 * @returns {Object} { data, error }
 */
export async function getUserCredits() {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('user_credits')
    .select('*')
    .eq('user_id', user.id)
    .single();

  return { data, error };
}

/**
 * Get user's credit transaction history
 * @param {number} limit - Number of transactions to fetch
 * @returns {Object} { data, error }
 */
export async function getCreditTransactions(limit = 50) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: userError || new Error('Not authenticated') };
  }

  const { data, error } = await supabase
    .from('credit_transactions')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .limit(limit);

  return { data, error };
}

// ============================================================================
// REAL-TIME SUBSCRIPTIONS
// ============================================================================

/**
 * Subscribe to leaderboard updates
 * @param {string} rivalryId - Rivalry UUID
 * @param {Function} callback - Callback function for updates
 * @returns {Object} Subscription object
 */
export function subscribeToLeaderboard(rivalryId, callback) {
  return supabase
    .channel(`rivalry-leaderboard-${rivalryId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'rivalry_participants',
        filter: `rivalry_id=eq.${rivalryId}`,
      },
      callback
    )
    .subscribe();
}

/**
 * Subscribe to chat messages
 * @param {string} rivalryId - Rivalry UUID
 * @param {Function} callback - Callback function for new messages
 * @returns {Object} Subscription object
 */
export function subscribeToMessages(rivalryId, callback) {
  return supabase
    .channel(`rivalry-chat-${rivalryId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'rivalry_messages',
        filter: `rivalry_id=eq.${rivalryId}`,
      },
      callback
    )
    .subscribe();
}
